import { create } from 'zustand';
import { supabase } from 'common/src/supabase';

type UniqueAttributesResponse = {
  varieties?: string[];
  calibers?: string[];
  labels?: string[];
  colors?: string[];
};

type SettlementsStore = {
  uniqueVarieties: string[];
  uniqueCalibers: string[];
  uniqueLabels: string[];
  uniqueColors: string[];
  loading: boolean;
  error: string | null;
  fetchUniqueAttributes: (params: {
    session: { access_token: string } | null;
    season?: string;
    speciesId?: number;
  }) => Promise<void>;
  clearUniqueAttributes: () => void;
};

const sortValues = (values: string[]) =>
  values
    .filter((value) => value && value.trim().length > 0)
    .sort((a, b) => a.localeCompare(b));

const useSettlementsStore = create<SettlementsStore>((set) => ({
  uniqueVarieties: [],
  uniqueCalibers: [],
  uniqueLabels: [],
  uniqueColors: [],
  loading: false,
  error: null,
  fetchUniqueAttributes: async ({ session, season, speciesId }) => {
    if (!session?.access_token || !season || !speciesId) {
      set({
        uniqueVarieties: [],
        uniqueCalibers: [],
        uniqueLabels: [],
        uniqueColors: [],
        loading: false,
        error: null,
      });
      return;
    }

    set({ loading: true, error: null });
    try {
      const { data, error } = await supabase
        .rpc('get_settlement_unique_attributes', {
          p_season: season,
          p_species_id: speciesId,
        });

      if (error) throw error;

      const attributes = (data || {}) as UniqueAttributesResponse;

      set({
        uniqueVarieties: sortValues(attributes.varieties || []),
        uniqueCalibers: sortValues(attributes.calibers || []),
        uniqueLabels: sortValues(attributes.labels || []),
        uniqueColors: sortValues(attributes.colors || []),
      });
    } catch (fetchError) {
      console.error('Error fetching unique settlement attributes:', fetchError);
      set({ error: 'Error fetching settlement attributes' });
    } finally {
      set({ loading: false });
    }
  },
  clearUniqueAttributes: () =>
    set({
      uniqueVarieties: [],
      uniqueCalibers: [],
      uniqueLabels: [],
      uniqueColors: [],
      error: null,
    }),
}));

export default useSettlementsStore;
